/**
 * Poll GET /jobs/{id} until done/error. Long LLM ops return { jobId } first.
 */

import { API_URL, fetchApi } from './client';

export type AsyncJobAccepted = { jobId: string; status?: string };

export type AsyncJobStatus<T = unknown> = {
  jobId: string;
  kind: string;
  status: string;
  error?: string | null;
  result?: T | null;
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function isTransientPollError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  if (/Session expired|401|403|404|Job not found|no result|Job failed|aborted/i.test(msg)) {
    return false;
  }
  return /HTTP (429|5\d\d)|Failed to fetch|NetworkError|network|timeout|TypeError/i.test(msg);
}

export async function awaitJob<T>(
  jobId: string,
  opts?: { intervalMs?: number; timeoutMs?: number; signal?: AbortSignal },
): Promise<T> {
  const intervalMs = opts?.intervalMs ?? 800;
  const timeoutMs = opts?.timeoutMs ?? 180_000;
  const started = Date.now();
  let lastErr: unknown;

  while (Date.now() - started < timeoutMs) {
    if (opts?.signal?.aborted) {
      throw new Error('Job polling aborted');
    }
    try {
      const job = await fetchApi<AsyncJobStatus<T>>(
        `${API_URL}/jobs/${encodeURIComponent(jobId)}`,
      );
      if (job.status === 'done') {
        if (job.result == null) {
          throw new Error('Job succeeded but returned no result');
        }
        return job.result as T;
      }
      if (job.status === 'error') {
        throw new Error(job.error || 'Job failed');
      }
      lastErr = null;
    } catch (err) {
      lastErr = err;
      const msg = err instanceof Error ? err.message : String(err);
      if (/Session expired|401|403|404|Job not found|no result/i.test(msg)) {
        throw err;
      }
      if (!isTransientPollError(err)) {
        throw err;
      }
    }
    await sleep(intervalMs);
  }
  if (lastErr) throw lastErr;
  throw new Error('Job timed out');
}

/** POST/GET that returns { jobId }, then poll until result. */
export async function fetchAsyncJob<T>(
  url: string,
  options?: RequestInit,
  pollOpts?: { intervalMs?: number; timeoutMs?: number; signal?: AbortSignal },
): Promise<T> {
  const accepted = await fetchApi<AsyncJobAccepted>(url, options);
  if (!accepted?.jobId) {
    throw new Error('No jobId in response');
  }
  return awaitJob<T>(accepted.jobId, pollOpts);
}
